import { apiFetchFull, api } from './client'

export interface AusverkaufAntwort {
  ausverkauf: boolean
  ausverkauf_override: boolean | null
  ausverkauf_quelle: 'dr' | 'hand' | null
  dr_sku: string | null
}

export function setAusverkauf(sku: string, wert: boolean | null) {
  return api.patch<AusverkaufAntwort>(`/stock/${encodeURIComponent(sku)}/ausverkauf/`, { wert })
}

// Backend-Response-Format (nested stock object)
/** Offene Vorab-Zeile eines Grundartikels (Plans/2026-10-19-vorab-bestand-plan.md §2.3). */
export interface VorabEintrag {
  id: number
  stueck: number
  receipt_id: number
  shipment_id: number
  created_at: string
  created_by_name: string | null
  evtl_doppelt: boolean
  dr_plus: number | null
}

interface StockItemRaw {
  ausverkauf?: boolean
  ausverkauf_override?: boolean | null
  ausverkauf_quelle?: 'dr' | 'hand' | null
  is_grundartikel?: boolean
  id: number
  sku: string
  barcode?: string | null
  mpn?: string | null
  name: string
  manufacturer?: string | null
  folder_id?: number | null
  category_id?: number | null
  merchant_sku?: string | null
  price: string
  tax: string
  rrp?: string | null
  supplier_id?: number | null
  supplier_price?: string | null
  weight_grams?: number | null
  size?: {
    length_mm?: number | null
    width_mm?: number | null
    height_mm?: number | null
  }
  condition_id?: number | null
  stock?: {
    quantity?: number
    quantity_alert?: number
    quantity_min?: number
    quantity_reserved?: number
    quantity_reserved_2?: number
    quantity_available?: number
    quantity_ebay?: number
    quantity_total?: number
    quantity_ordered?: number | null
    quantity_ordered_date?: string | null
    vorab_offen?: number
    bestand_plattform?: number
    vorab_eintraege?: VorabEintrag[]
  }
  location_fields?: string[]
  is_fulfillment?: boolean
  is_active?: boolean
  infos?: string | null
  freetext?: {
    '1'?: string | null
    '2'?: string | null
    '3'?: string | null
    '4'?: string | null
    '5'?: string | null
  }
  stock_source_id?: number | null
  stock_source_sku?: string | null
  dr_stock_id?: string | null
  dr_sku?: string | null
  alarm_state?: 'alert' | 'critical' | null
  dr_supplier_id?: string | null
  content_quantity?: number | null
  content_type?: string | null
  content_basic_price_type?: string | null
  customs_description?: string | null
  customs_hs_code?: string | null
  customs_origin_country?: string | null
  cross_selling_active?: boolean
  cross_selling_quantity_split?: boolean
  cross_selling_desc_national_id?: number | null
  cross_selling_desc_intl_id?: number | null
  shipping_infos?: string | null
  created_at?: string
  updated_at?: string
}

export interface StockItem {
  ausverkauf?: boolean
  ausverkauf_override?: boolean | null
  ausverkauf_quelle?: 'dr' | 'hand' | null
  is_grundartikel?: boolean
  id: number
  sku: string
  barcode?: string
  mpn?: string
  name: string
  manufacturer?: string
  folder_id?: number | null
  category_id?: number | null
  merchant_sku?: string | null
  // Preise
  price: string
  tax: string
  rrp?: string | null
  supplier_id?: number | null
  supplier_price?: string | null
  // Inhalt / Grundpreis (DR 0019)
  content_quantity?: number | null
  content_type?: string | null
  content_basic_price_type?: string | null
  // Physisch
  weight_grams?: number | null
  size_length_mm?: number | null
  size_width_mm?: number | null
  size_height_mm?: number | null
  condition_id?: number | null
  // Bestand
  quantity: number
  quantity_reserved: number
  quantity_reserved_2?: number
  quantity_available: number
  quantity_ebay: number
  quantity_total: number
  quantity_alert?: number
  quantity_min?: number
  quantity_ordered?: number | null
  quantity_ordered_date?: string | null
  // Plan 2026-10-19 S2: Vorab-Bestand aus dem Wareneingang (nur Grundartikel; Kinder/Sets 0).
  vorab_offen?: number
  bestand_plattform?: number
  vorab_eintraege?: VorabEintrag[]
  // Logistik
  location_fields: string[]
  is_fulfillment?: boolean
  // Status
  is_active: boolean
  alarm_state?: 'alert' | 'critical' | null
  // Texte
  infos?: string | null
  freetext_1?: string | null
  freetext_2?: string | null
  freetext_3?: string | null
  freetext_4?: string | null
  freetext_5?: string | null
  shipping_infos?: string | null
  // DR / Grundartikel
  stock_source_id?: number | null
  stock_source_sku?: string | null
  dr_stock_id?: string | null
  dr_sku?: string | null
  dr_supplier_id?: string | null
  // Zoll
  customs_description?: string | null
  customs_hs_code?: string | null
  customs_origin_country?: string | null
  // Cross-Selling
  cross_selling_active?: boolean
  cross_selling_quantity_split?: boolean
  cross_selling_desc_national_id?: number | null
  cross_selling_desc_intl_id?: number | null
  created_at: string
  updated_at: string
}

function flattenStockItem(raw: StockItemRaw): StockItem {
  const available = raw.stock?.quantity_available ?? 0
  const ebay = raw.stock?.quantity_ebay ?? 0
  return {
    id: raw.id,
    ausverkauf: raw.ausverkauf ?? false,
    ausverkauf_override: raw.ausverkauf_override ?? null,
    ausverkauf_quelle: raw.ausverkauf_quelle ?? null,
    is_grundartikel: raw.is_grundartikel ?? false,
    sku: raw.sku,
    barcode: raw.barcode ?? undefined,
    mpn: raw.mpn ?? undefined,
    name: raw.name,
    manufacturer: raw.manufacturer ?? undefined,
    folder_id: raw.folder_id ?? null,
    category_id: raw.category_id ?? null,
    merchant_sku: raw.merchant_sku ?? null,
    price: raw.price,
    tax: raw.tax,
    rrp: raw.rrp ?? null,
    supplier_id: raw.supplier_id ?? null,
    supplier_price: raw.supplier_price ?? null,
    content_quantity: raw.content_quantity ?? null,
    content_type: raw.content_type ?? null,
    content_basic_price_type: raw.content_basic_price_type ?? null,
    weight_grams: raw.weight_grams ?? null,
    size_length_mm: raw.size?.length_mm ?? null,
    size_width_mm: raw.size?.width_mm ?? null,
    size_height_mm: raw.size?.height_mm ?? null,
    condition_id: raw.condition_id ?? null,
    quantity: raw.stock?.quantity ?? 0,
    quantity_reserved: raw.stock?.quantity_reserved ?? 0,
    quantity_reserved_2: raw.stock?.quantity_reserved_2 ?? 0,
    quantity_available: available,
    quantity_ebay: ebay,
    quantity_total: raw.stock?.quantity_total ?? available + ebay,
    quantity_alert: raw.stock?.quantity_alert,
    quantity_min: raw.stock?.quantity_min ?? 0,
    quantity_ordered: raw.stock?.quantity_ordered ?? null,
    quantity_ordered_date: raw.stock?.quantity_ordered_date ?? null,
    vorab_offen: raw.stock?.vorab_offen ?? 0,
    bestand_plattform: raw.stock?.bestand_plattform ?? raw.stock?.quantity ?? 0,
    vorab_eintraege: raw.stock?.vorab_eintraege ?? [],
    location_fields: raw.location_fields ?? [],
    is_fulfillment: raw.is_fulfillment ?? false,
    is_active: raw.is_active ?? true,
    alarm_state: raw.alarm_state ?? null,
    infos: raw.infos ?? null,
    freetext_1: raw.freetext?.['1'] ?? null,
    freetext_2: raw.freetext?.['2'] ?? null,
    freetext_3: raw.freetext?.['3'] ?? null,
    freetext_4: raw.freetext?.['4'] ?? null,
    freetext_5: raw.freetext?.['5'] ?? null,
    shipping_infos: raw.shipping_infos ?? null,
    stock_source_id: raw.stock_source_id ?? null,
    stock_source_sku: raw.stock_source_sku ?? null,
    dr_stock_id: raw.dr_stock_id ?? null,
    dr_sku: raw.dr_sku ?? null,
    dr_supplier_id: raw.dr_supplier_id ?? null,
    customs_description: raw.customs_description ?? null,
    customs_hs_code: raw.customs_hs_code ?? null,
    customs_origin_country: raw.customs_origin_country ?? null,
    cross_selling_active: raw.cross_selling_active ?? false,
    cross_selling_quantity_split: raw.cross_selling_quantity_split ?? false,
    cross_selling_desc_national_id: raw.cross_selling_desc_national_id ?? null,
    cross_selling_desc_intl_id: raw.cross_selling_desc_intl_id ?? null,
    created_at: raw.created_at ?? '',
    updated_at: raw.updated_at ?? '',
  }
}

export interface StockListParams {
  search?: string
  merchant_sku?: string
  min_id?: number
  limit?: number
  sort?: string
  order?: 'asc' | 'desc'
  is_grundartikel?: boolean
}

export interface StockListResult {
  items: StockItem[]
  has_more: boolean
  next_min_id?: number
}

export async function listStock(params: StockListParams = {}): Promise<StockListResult> {
  const qs = new URLSearchParams()
  if (params.search) {
    // Mehrfeld-Suche (Backend-Spezialfilter): Name ∨ SKU ∨ Händlerartikelnummer ∨ Barcode
    qs.set('filter[search][like]', params.search)
  }
  if (params.merchant_sku) qs.set('filter[merchant_sku][eq]', params.merchant_sku)
  if (params.is_grundartikel) qs.set('filter[is_grundartikel][eq]', '1')
  if (params.min_id) qs.set('pagination[min_id]', String(params.min_id))
  if (params.limit) qs.set('pagination[limit]', String(params.limit))
  if (params.sort) qs.set('sort', params.sort)
  if (params.order) qs.set('order', params.order)

  const path = `/stock/${qs.toString() ? '?' + qs.toString() : ''}`
  const res = await apiFetchFull<StockItemRaw[]>(path)
  return {
    items: (res.data ?? []).map(flattenStockItem),
    has_more: res.pagination?.has_more ?? false,
    next_min_id: res.pagination?.next_min_id as number | undefined,
  }
}

export async function getStockItem(sku: string): Promise<StockItem> {
  const res = await api.get<{ item: StockItemRaw }>(`/stock/${encodeURIComponent(sku)}/`)
  return flattenStockItem(res.item)
}

export type StockItemUpdatePayload = Partial<{
  name: string
  barcode: string | null
  mpn: string | null
  manufacturer: string | null
  folder_id: number | null
  category_id: number | null
  price: number
  tax: number
  rrp: number | null
  supplier_id: number | null
  merchant_sku: string | null
  supplier_price: number | null
  weight_grams: number | null
  size_length_mm: number | null
  size_width_mm: number | null
  size_height_mm: number | null
  condition_id: number
  quantity_alert: number
  quantity_min: number
  is_fulfillment: boolean
  is_active: boolean
  infos: string | null
  freetext_1: string | null
  freetext_2: string | null
  freetext_3: string | null
  freetext_4: string | null
  freetext_5: string | null
  content_quantity: number | null
  content_type: string | null
  content_basic_price_type: string | null
  customs_description: string | null
  customs_hs_code: string | null
  customs_origin_country: string | null
  dr_supplier_id: string | null
  cross_selling_active: boolean
  cross_selling_quantity_split: boolean
  cross_selling_desc_national_id: number | null
  cross_selling_desc_intl_id: number | null
  shipping_infos: string | null
  location_fields: string[]
}>

export async function updateStockItem(
  sku: string,
  fields: StockItemUpdatePayload,
): Promise<StockItem> {
  const res = await api.post<{ item: StockItemRaw }>(
    `/stock/${encodeURIComponent(sku)}/`,
    fields,
  )
  return flattenStockItem(res.item)
}

export async function adjustInventory(
  sku: string,
  delta: number,
  note?: string,
): Promise<StockItem> {
  const res = await api.post<{ item: StockItemRaw }>(`/stock/${encodeURIComponent(sku)}/inventory/`, {
    delta,
    note,
  })
  return flattenStockItem(res.item)
}

export interface StockMovement {
  id: number
  type: string
  quantity: number
  quantity_before: number
  quantity_after: number
  note: string | null
  order_id: number | null
  actor_id: number | null
  actor_type: string | null
  created_at: string
}

export interface StockHistoryResult {
  data: StockMovement[]
  pagination: { page: number; limit: number; total: number; has_more: boolean }
}

export async function getStockHistory(
  sku: string,
  params: { page?: number; limit?: number } = {},
): Promise<StockHistoryResult> {
  const qs = new URLSearchParams()
  if (params.page) qs.set('pagination[page]', String(params.page))
  if (params.limit) qs.set('pagination[limit]', String(params.limit))
  const path = `/stock/${encodeURIComponent(sku)}/history/${qs.toString() ? '?' + qs.toString() : ''}`
  const res = await apiFetchFull<StockMovement[]>(path)
  return {
    data: res.data ?? [],
    pagination: {
      page: (res.pagination?.['page' as keyof typeof res.pagination] as number) ?? 1,
      limit: (res.pagination?.['limit' as keyof typeof res.pagination] as number) ?? 50,
      total: res.pagination?.total ?? 0,
      has_more: res.pagination?.has_more ?? false,
    },
  }
}

// ============================================================
//  Bulk-Aktionen (Phase 4)
// ============================================================

export interface BulkResult {
  updated: number
  skipped: number
}

export async function bulkMove(skus: string[], folder_id: number): Promise<BulkResult> {
  return api.post<BulkResult>('/stock/bulk/move/', { skus, folder_id })
}

export async function bulkPricing(
  skus: string[],
  payload: { price_delta_pct?: number; fixed_price?: number },
): Promise<BulkResult & { mode: 'fixed' | 'delta_pct' }> {
  return api.post<BulkResult & { mode: 'fixed' | 'delta_pct' }>('/stock/bulk/pricing/', {
    skus,
    ...payload,
  })
}

export async function bulkStatus(skus: string[], is_active: boolean): Promise<BulkResult> {
  return api.post<BulkResult>('/stock/bulk/status/', { skus, is_active })
}

export async function searchByBarcode(barcode: string): Promise<StockListResult> {
  const qs = new URLSearchParams()
  qs.set('filter[barcode][eq]', barcode)
  const res = await apiFetchFull<StockItem[]>(`/stock/?${qs.toString()}`)
  return {
    items: res.data ?? [],
    has_more: false,
  }
}

export interface StockFolder {
  id: number
  name: string
  parent_id: number | null
}

export async function getFolders(): Promise<StockFolder[]> {
  const res = await apiFetchFull<StockFolder[]>('/stock/folders/')
  return res.data ?? []
}

// Laedt ALLE Grundartikel (Pull-Set) per Page-Loop. Route paginiert offset-basiert
// ueber pagination[page]; min_id wird von GET /stock/ NICHT als Filter angewendet.
//
// WICHTIG (Stabilitaet): Der Default-Sort der Route ist created_at DESC. created_at ist
// NICHT eindeutig (Bulk-Import: bis zu 164 Grundartikel teilen einen Zeitstempel) → bei
// OFFSET-Pagination liefert Postgres pro Query eine andere Reihenfolge, die Seiten
// ueberlappen und ~85 Items werden doppelt geladen (andere fehlen). Deshalb explizit
// nach der eindeutigen Spalte `sku` sortieren → deterministische, ueberlappungsfreie
// Seitengrenzen. Zusaetzlich Dedupe nach id als Sicherheitsnetz.
export async function listAllGrundartikel(): Promise<StockItem[]> {
  const LIMIT = 200
  const MAX_PAGES = 50 // Endlosschleifen-Schutz (50 * 200 = 10.000 Items)
  const byId = new Map<number, StockItem>()
  for (let page = 1; page <= MAX_PAGES; page++) {
    const qs = new URLSearchParams()
    qs.set('filter[is_grundartikel][eq]', '1')
    // Backend erwartet sort[field]=asc|desc (Objekt-Format, filter-dsl parseSort).
    // sku ist eindeutig → deterministische, ueberlappungsfreie Offset-Pagination.
    qs.set('sort[sku]', 'asc')
    qs.set('pagination[page]', String(page))
    qs.set('pagination[limit]', String(LIMIT))
    const res = await apiFetchFull<StockItemRaw[]>(`/stock/?${qs.toString()}`)
    const items = (res.data ?? []).map(flattenStockItem)
    for (const it of items) byId.set(it.id, it)
    if (!res.pagination?.has_more || items.length === 0) break
    if (page === MAX_PAGES) {
      console.warn(`listAllGrundartikel: MAX_PAGES (${MAX_PAGES}) erreicht — Ergebnis evtl. unvollstaendig`)
    }
  }
  return [...byId.values()]
}
